import { EntryRow } from './EntryRow';
import { formatDayMonth, formatFullDate, formatWeekday, plural, relativeDay } from '../format';
import type { EventWithEntries } from '../types';

interface EventCardProps {
  event: EventWithEntries;
  expanded: boolean;
  pendingUserIds: ReadonlySet<string>;
  onToggleExpanded: (eventId: string) => void;
  onToggleTransport: (eventId: string, userId: string, transport: boolean) => void;
}

export function EventCard({
  event,
  expanded,
  pendingUserIds,
  onToggleExpanded,
  onToggleTransport,
}: EventCardProps) {
  const panelId = `event-panel-${event.id}`;
  const subtitle = [event.place, event.discipline, event.level].filter(Boolean).join(' · ');
  // Štafety a družstva: ORIS u nich jmenný seznam nevrací, jde jen o počet týmů.
  const teamsOnly = event.entryCount === 0 && event.teamEntryCount > 0;
  const regions = shortenRegions(event.region);

  return (
    <article className={`card${expanded ? ' card--open' : ''}`}>
      {/*
        Odkazy nesmí být uvnitř rozbalovacího tlačítka (vnořené interaktivní prvky).
        Tlačítko je proto neviditelná vrstva přes celou hlavičku a obsah leží nad ním –
        klikací zůstávají jen ikony. Díky tomu se vejdou na řádek s chipy a název má
        plnou šířku karty.
      */}
      <div className="card__head">
        <button
          type="button"
          className="card__summary"
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={`${event.name}, ${formatFullDate(event.date)} – přihlášení na dopravu`}
          onClick={() => onToggleExpanded(event.id)}
        />

        <span className="card__date" aria-hidden="true">
          <span className="card__day">{formatDayMonth(event.date)}</span>
          <span className="card__weekday">{formatWeekday(event.date)}</span>
        </span>

        <div className="card__body">
          <h2 className="card__title">
            {event.name}
            {event.cancelled && <span className="badge badge--danger">Zrušeno</span>}
          </h2>
          <p className="card__subtitle">
            <span className="card__subtitle-text">{subtitle || event.organiser}</span>
            {regions && (
              <span className="card__region" title={regions.full}>
                {regions.short}
              </span>
            )}
          </p>

          <div className="card__tags">
            <span className="chip">{relativeDay(event.date)}</span>
            {teamsOnly ? (
              <span className="chip">
                {event.teamEntryCount} {plural(event.teamEntryCount, 'štafeta', 'štafety', 'štafet')}
              </span>
            ) : (
              <span className={`chip${event.transportCount > 0 ? ' chip--active' : ''}`}>
                <BusIcon />
                {event.transportCount} / {event.entryCount}
              </span>
            )}

            <span className="card__tools">
              <a
                className="iconbutton"
                href={event.orisUrl}
                target="_blank"
                rel="noreferrer"
                title="Detail závodu v ORISu"
                aria-label={`Detail závodu ${event.name} v ORISu`}
              >
                <OrisIcon />
              </a>
              {event.mapUrl ? (
                <a
                  className="iconbutton"
                  href={event.mapUrl}
                  target="_blank"
                  rel="noreferrer"
                  title="Turistická mapa místa konání"
                  aria-label={`Turistická mapa místa konání závodu ${event.name}`}
                >
                  <MapIcon />
                </a>
              ) : (
                <span
                  className="iconbutton iconbutton--muted"
                  title="ORIS u závodu neuvádí souřadnice"
                >
                  <MapIcon />
                </span>
              )}
            </span>
          </div>
        </div>

        <span className="card__chevron" aria-hidden="true">
          <ChevronIcon />
        </span>
      </div>

      {/* Zavřený panel je `inert`, aby se do skrytých přepínačů nedalo dostat tabulátorem. */}
      <div className={`card__panel${expanded ? ' card__panel--open' : ''}`} inert={!expanded}>
        <div className="card__panel-inner">
          <div id={panelId} className="panel" role="group" aria-label={`Přihlášení – ${event.name}`}>
            {teamsOnly ? (
              <p className="panel__note">
                ORIS u tohoto závodu eviduje {event.teamEntryCount}{' '}
                {plural(event.teamEntryCount, 'přihlášku', 'přihlášky', 'přihlášek')} štafet bez jmen
                závodníků, dopravu tu proto nelze zaškrtávat.
              </p>
            ) : (
              <>
                <div className="panel__counts">
                  <div className="count count--primary">
                    <span className="count__value">{event.transportCount}</span>
                    <span className="count__label">na dopravu</span>
                  </div>
                  <div className="count">
                    <span className="count__value">{event.entryCount}</span>
                    <span className="count__label">
                      {plural(event.entryCount, 'přihlášený', 'přihlášení', 'přihlášených')}
                    </span>
                  </div>
                </div>

                <ul className="panel__list">
                  {event.entries.map((entry) => (
                    <EntryRow
                      key={entry.userId}
                      entry={entry}
                      pending={pendingUserIds.has(entry.userId)}
                      onToggle={(userId, transport) => onToggleTransport(event.id, userId, transport)}
                    />
                  ))}
                </ul>

                {event.teamEntryCount > 0 && (
                  <p className="panel__note panel__note--small">
                    Navíc {event.teamEntryCount}{' '}
                    {plural(event.teamEntryCount, 'přihláška', 'přihlášky', 'přihlášek')} štafet, které
                    ORIS uvádí bez jmen.
                  </p>
                )}
              </>
            )}

          </div>
        </div>
      </div>
    </article>
  );
}

function BusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M5 17V7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v10" strokeLinecap="round" />
      <path d="M5 11h14M9 5v6M15 5v6M4 17h16" strokeLinecap="round" />
      <circle cx="8" cy="19" r="1.5" />
      <circle cx="16" cy="19" r="1.5" />
    </svg>
  );
}

/**
 * ORIS posílá oblasti jako „ČR, JČ, VČ, VY, ZČ“. Delší výčty by na mobilu vytlačily
 * disciplínu z řádku, proto se zkrátí na první oblast a počet zbývajících.
 */
function shortenRegions(region: string): { short: string; full: string } | null {
  const parts = region
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;

  const full = parts.join(', ');
  const short = parts.length > 2 ? `${parts[0]} +${parts.length - 1}` : full;
  return { short, full };
}

function OrisIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M14 4h6v6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 4 11 13" strokeLinecap="round" />
      <path
        d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="2.6" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
